import { useCallback, useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router";

import { Box, Stack } from "@mui/material";
import { ClientTabBase } from "@shared";

import { CaseNoteResponse, ClientDetailResponse } from "@/api";
import { SearchField } from "@/common/components/search-field/SearchField";
import { ClientStatusWidget } from "@/pages/clients/client/components/client-status-widget/ClientStatusWidget";
import { SEARCH_PARAM } from "@/pages/clients/client/hooks/useClientTabs";
import { CASE_NOTE_ID_PARAM } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { CaseNoteListFilter } from "./CaseNotes.types";
import { CaseNoteActionButtons } from "./components/case-note-action-buttons/CaseNoteActionButtons";
import { CaseNotesFilterMenu } from "./components/case-notes-filter-menu/CaseNotesFilterMenu";
import { CaseNotesList } from "./components/case-notes-list/CaseNotesList";
import { CaseNoteOverview } from "./components/case-notes-overview/CaseNoteOverview";
import { useCaseNotes } from "./hooks/useCaseNotes";

// Mirrors GetCaseNotesByClientIdQueryValidator: the literal core (a single leading/trailing
// * wildcard stripped) must have at least this many characters — shorter terms would be
// rejected with a 400, surfacing as a generic error toast
const MinSearchLength = 2;

const FILTER_PARAM = "filter";

function hasSearchableCore(searchValue: string): boolean {
  return searchValue.replace(/^\*/, "").replace(/\*$/, "").length >= MinSearchLength;
}

export function CaseNotesTab() {
  const client = useOutletContext<ClientDetailResponse>();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSearchParam = searchParams.get(SEARCH_PARAM) ?? "";
  const selectedCaseNoteId = searchParams.get(CASE_NOTE_ID_PARAM) ?? undefined;
  const filter =
    searchParams.get(FILTER_PARAM) === CaseNoteListFilter.AppointmentNotesOnly
      ? CaseNoteListFilter.AppointmentNotesOnly
      : CaseNoteListFilter.All;

  const { caseNotes } = useCaseNotes(client, initialSearchParam, filter);

  // The URL is the single source of truth for the selected note (shareable, refresh-safe): derive
  // the selection from the id, falling back to the newest note.
  const selectedCaseNote = caseNotes?.find((n) => n.caseNoteId === selectedCaseNoteId) ?? caseNotes?.[0] ?? null;

  const setCaseNoteParam = useCallback(
    (note: CaseNoteResponse | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          if (note) {
            params.set(CASE_NOTE_ID_PARAM, note.caseNoteId);
          } else {
            params.delete(CASE_NOTE_ID_PARAM);
          }

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Normalise the URL to the effective selection: on first load (no id) or when the linked/selected
  // note no longer exists (e.g. after a delete, where the list falls back to the newest note), point
  // the URL at what's actually shown so it never lingers on a missing or empty selection.
  useEffect(() => {
    if (!caseNotes) {
      return;
    }

    if (selectedCaseNote?.caseNoteId !== selectedCaseNoteId) {
      setCaseNoteParam(selectedCaseNote);
    }
  }, [caseNotes, selectedCaseNote, selectedCaseNoteId, setCaseNoteParam]);

  const runSearch = (searchValue: string | null) => {
    if (searchValue && !hasSearchableCore(searchValue)) {
      return;
    }

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      if (searchValue) {
        params.set(SEARCH_PARAM, searchValue);
      } else {
        params.delete(SEARCH_PARAM);
      }

      return params;
    });
  };

  const changeFilter = (nextFilter: CaseNoteListFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      if (nextFilter === CaseNoteListFilter.All) {
        params.delete(FILTER_PARAM);
      } else {
        params.set(FILTER_PARAM, nextFilter);
      }

      return params;
    });
  };

  return (
    <ClientTabBase
      tabActions={<CaseNoteActionButtons client={client} selectedCaseNote={selectedCaseNote} />}
      navigation={
        <Stack gap={2} overflow={"hidden"}>
          <Box display={"flex"}>
            <SearchField
              placeholder={t("clients.client.caseNotes.searchPlaceholder")}
              onChange={runSearch}
              initialValue={initialSearchParam}
              width="100%"
            />
            <CaseNotesFilterMenu filter={filter} onChange={changeFilter} />
          </Box>
          <CaseNotesList caseNotes={caseNotes} selectedCaseNote={selectedCaseNote} onSelect={setCaseNoteParam} />
        </Stack>
      }
      navigationFooter={<ClientStatusWidget client={client} />}
    >
      <CaseNoteOverview selectedCaseNote={selectedCaseNote} client={client} onDeleted={() => setCaseNoteParam(null)} />
    </ClientTabBase>
  );
}
